import React from 'react';
import { ExternalLink } from 'lucide-react';
import type { NewsArticle } from '../../types/market';

interface NewsCardProps {
  article: NewsArticle;
  showSentiment?: boolean;
  compact?: boolean;
}

const sentimentMap = {
  positive: { color: '#10b981', bg: 'rgba(16,185,129,0.1)',  border: 'rgba(16,185,129,0.25)', icon: '↗', label: 'Positive' },
  negative: { color: '#ef4444', bg: 'rgba(239,68,68,0.1)',   border: 'rgba(239,68,68,0.25)',  icon: '↘', label: 'Negative' },
  neutral:  { color: '#94a3b8', bg: 'rgba(148,163,184,0.1)', border: 'rgba(148,163,184,0.2)', icon: '→', label: 'Neutral'  },
} as const;

function timeAgo(dateStr: string): string {
  const mins = Math.floor((Date.now() - new Date(dateStr).getTime()) / 60000);
  if (mins < 1)  return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24)  return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

export const NewsCard: React.FC<NewsCardProps> = ({
  article,
  showSentiment = true,
  compact = false,
}) => {
  const sm = sentimentMap[article.sentiment as keyof typeof sentimentMap] ?? sentimentMap.neutral;

  const open = () => window.open(article.url, '_blank', 'noopener,noreferrer');

  /* ── Compact row (used in lists) ── */
  if (compact) {
    return (
      <div
        onClick={open}
        style={{
          display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
          gap: '0.75rem',
          padding: '0.6875rem 0',
          borderBottom: '1px solid #0f1929',
          cursor: 'pointer',
          transition: 'background 0.15s',
        }}
        onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.02)'; }}
        onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{
            fontSize: '0.8125rem', fontWeight: 600,
            color: '#cbd5e1', lineHeight: 1.5,
            display: '-webkit-box', WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical', overflow: 'hidden',
            marginBottom: '0.25rem',
            transition: 'color 0.15s',
          }}
            onMouseEnter={e => { e.currentTarget.style.color = '#60a5fa'; }}
            onMouseLeave={e => { e.currentTarget.style.color = '#cbd5e1'; }}
          >
            {article.title}
          </p>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            <span style={{ fontSize: '0.7rem', fontWeight: 600, color: '#334155' }}>{article.source}</span>
            <span style={{ color: '#1e2d45', fontSize: '0.6rem' }}>•</span>
            <span style={{ fontSize: '0.7rem', color: '#1e3a5f' }}>{timeAgo(article.published_at)}</span>
          </div>
        </div>
        {showSentiment && article.sentiment && (
          <span style={{
            flexShrink: 0,
            padding: '0.1875rem 0.45rem', borderRadius: 99,
            background: sm.bg, border: `1px solid ${sm.border}`,
            fontSize: '0.7rem', fontWeight: 700, color: sm.color,
          }}>
            {sm.icon}
          </span>
        )}
      </div>
    );
  }

  /* ── Full card ── */
  return (
    <div
      onClick={open}
      style={{
        background: '#1a2235',
        border: '1px solid #1e2d45',
        borderRadius: 10,
        padding: '1rem',
        cursor: 'pointer',
        transition: 'border-color 0.18s, background 0.18s',
      }}
      onMouseEnter={e => {
        e.currentTarget.style.borderColor = '#2a3a55';
        e.currentTarget.style.background = '#1f2d44';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.borderColor = '#1e2d45';
        e.currentTarget.style.background = '#1a2235';
      }}
    >
      {/* Title row */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.75rem', marginBottom: '0.5rem' }}>
        <h3 style={{
          fontSize: '0.875rem', fontWeight: 600, color: '#e2e8f0',
          lineHeight: 1.5, flex: 1,
          display: '-webkit-box', WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical', overflow: 'hidden',
          letterSpacing: '-0.005em',
        }}>
          {article.title}
        </h3>
        {showSentiment && article.sentiment && (
          <span style={{
            flexShrink: 0,
            padding: '0.2rem 0.55rem', borderRadius: 99,
            background: sm.bg, border: `1px solid ${sm.border}`,
            fontSize: '0.7rem', fontWeight: 700, color: sm.color,
            whiteSpace: 'nowrap',
          }}>
            {sm.icon} {sm.label}
          </span>
        )}
      </div>

      {/* Snippet */}
      {article.snippet && (
        <p style={{
          fontSize: '0.78rem', color: '#334155', lineHeight: 1.6, marginBottom: '0.75rem',
          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
        }}>
          {article.snippet}
        </p>
      )}

      {/* Footer */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
          <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#475569' }}>{article.source}</span>
          <span style={{ color: '#1e2d45', fontSize: '0.6rem' }}>•</span>
          <span style={{ fontSize: '0.72rem', color: '#1e3a5f' }}>{timeAgo(article.published_at)}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.72rem', fontWeight: 600, color: '#334155' }}>
          Read more <ExternalLink size={11} strokeWidth={2} />
        </div>
      </div>
    </div>
  );
};
